import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useCustomHotkeys } from '../../../../common/hooks/useCustomHotkeys';
import { EditorMode } from '../../../../modules/editor/models/EditorModes';
import { AnnotationConfirmationModal, AnnotationConfirmationModalMode } from '../../../components/AnnotationConfirmationModal';
import { SaveBeforeRejectConfirmationModal } from '../../../components/SaveBeforeRejectConfirmationModal';
import { doNothingAsync } from '../../../helpers/function.helpers';
import { useLoader } from '../../../helpers/useLoader';
import { useAnnotationPermissions } from '../annotation.permissions';
import { IAnnotationReviewService } from '../annotationReview.service';
import { IAnnotationsStore } from '../annotations.store';
import { ReviewNavigation } from '../components/ReviewNavigation';
import { IReviewRejectReasonsStore } from '../submodules/reviewRejectReasones/reviewRejectReasons.store';

export const ReviewControlsContainer = observer((): ReactElement => {
  const annotationReviewService = useInjection(IAnnotationReviewService.$);
  const reviewRejectReasonsStore = useInjection(IReviewRejectReasonsStore.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);
  const permissions = useAnnotationPermissions();

  const [showAnnotationConfirmationModal, setShowAnnotationConfirmationModal] = useState(false);
  const [annotationConfirmationModalMode, setAnnotationConfirmationModalMode] = useState(AnnotationConfirmationModalMode.ACCEPT);
  const [showDiscardConfirmationModal, setShowDiscardConfirmationModal] = useState(false);
  const [showSaveBeforeRejectConfirmationModal, setShowSaveBeforeRejectConfirmationModal] = useState(false);
  const { isLoading, withLoaderAsync } = useLoader();

  const handleAccept = async (): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => annotationReviewService.acceptAnnotationAsync(), 'accept-button');
  };

  const handleReject = async (): Promise<void> => {
    setShowSaveBeforeRejectConfirmationModal(false);
    toggleAnnotationConfirmationModal(AnnotationConfirmationModalMode.REJECT);
    return Promise.resolve();
  };

  const handleConfirmReject = async (reason?: string): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    if (reason) {
      await handleRejectConfirmed(reason);
    } else {
      setShowSaveBeforeRejectConfirmationModal(true);
    }
  };

  const handleRejectConfirmed = async (reason?: string): Promise<void> => {
    setShowSaveBeforeRejectConfirmationModal(false);
    await withLoaderAsync(async () => annotationReviewService.rejectAnnotationAsync(reason), 'reject-button');
  };

  const handleSaveBeforeRejectConfirmed = async (): Promise<void> => {
    await handleRejectConfirmed();
  };

  const handleRejectCanceled = (): void => {
    setShowSaveBeforeRejectConfirmationModal(false);
    setShowAnnotationConfirmationModal(false);
  };

  const handleEdit = async (): Promise<void> => {
    annotationReviewService.navigateToCorrect();
    return Promise.resolve();
  };

  const toggleAnnotationConfirmationModal = (mode?: AnnotationConfirmationModalMode): void => {
    if (mode !== undefined) {
      setAnnotationConfirmationModalMode(mode);
    }
    setShowAnnotationConfirmationModal((prevState) => !prevState);
  };

  const handleDiscardConfirmed = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    setShowDiscardConfirmationModal(false);
    await withLoaderAsync(async () => annotationReviewService.discardAnnotationsAsync(), 'discard-annotation-button');
  };

  const handleDiscardCanceled = (): void => {
    setShowDiscardConfirmationModal(false);
  };

  const handleDiscard = (): void => {
    setShowDiscardConfirmationModal(true);
  };

  const { uiStore } = annotationReviewService;
  const disabled = isLoading || uiStore.isImageLoading || annotationsStore.image === undefined;

  useCustomHotkeys(
    'ctrl+shift+enter',
    () => {
      toggleAnnotationConfirmationModal(AnnotationConfirmationModalMode.ACCEPT);
    },
    { enabled: !disabled },
    [toggleAnnotationConfirmationModal]
  );

  useCustomHotkeys(
    'ctrl+shift+backspace',
    () => {
      toggleAnnotationConfirmationModal(AnnotationConfirmationModalMode.REJECT);
    },
    { enabled: !disabled },
    [toggleAnnotationConfirmationModal]
  );

  useCustomHotkeys(
    ['e', 'E'],
    () => {
      void handleEdit();
    },
    { enabled: !disabled },
    [handleEdit]
  );

  return (
    <>
      <ReviewNavigation
        disabled={disabled}
        canDiscard={permissions.canDiscard().allowed}
        editorMode={EditorMode.BATCH_REVIEW}
        onAccept={handleAccept}
        onReject={handleReject}
        onEdit={handleEdit}
        onDiscardCanceled={handleDiscardCanceled}
        onDiscardConfirmed={handleDiscardConfirmed}
        onDiscard={handleDiscard}
        showDiscardModal={showDiscardConfirmationModal}
        canCancel={false}
        onCancel={doNothingAsync}
      />
      <AnnotationConfirmationModal
        showAnnotationConfirmationModal={showAnnotationConfirmationModal}
        annotationConfirmationModalMode={annotationConfirmationModalMode}
        rejectAutoCompleteOptions={reviewRejectReasonsStore.rejectionReasons.slice()}
        onAnnotationConfirm={annotationConfirmationModalMode === AnnotationConfirmationModalMode.ACCEPT ? handleAccept : handleConfirmReject}
        onAnnotationCancel={toggleAnnotationConfirmationModal}
      />
      <SaveBeforeRejectConfirmationModal
        onSaveBeforeRejectCancel={handleRejectCanceled}
        onSaveBeforeRejectConfirm={handleSaveBeforeRejectConfirmed}
        onSaveBeforeRejectDecline={handleReject}
        showSaveBeforeRejectModal={showSaveBeforeRejectConfirmationModal}
      />
    </>
  );
});
